import { inject, InjectionToken } from '@angular/core';
import { isObservable, Observable, of } from 'rxjs';
import { AiAssistantConfig, AssistantFieldLockMap, NO_FIELD_LOCKS } from './interfaces/config.interface';
import { AiAssistant, AiConnection, NamespaceConfig } from './interfaces/ai-assistant.interface';
import { PlatformLocation } from './ai-assistant.constants';

export const AI_ASSISTANT_CONFIG_TOKEN = new InjectionToken<AiAssistantConfig>(
  '[AiAssistant]: token for configuration (wire up with provideAiAssistant in the application ROOT providers)',
);

function asObservable<T>(value: Observable<T> | T | undefined | null): Observable<T> {
  return (isObservable(value) ? value : of(value)) as Observable<T>;
}

function fromConfig<T>(select: (config: AiAssistantConfig) => Observable<T> | T | undefined | null): Observable<T> {
  return asObservable(select(inject(AI_ASSISTANT_CONFIG_TOKEN)));
}

function fromOptionalConfig<T>(select: (config: AiAssistantConfig) => T): T | null {
  const config = inject(AI_ASSISTANT_CONFIG_TOKEN, { optional: true });
  return config ? select(config) : null;
}

export const ACCOUNT_GROUP_ID_TOKEN: InjectionToken<Observable<string>> = new InjectionToken(
  '[AiAssistant]: token for accountGroupId',
  { providedIn: 'root', factory: () => fromConfig((c) => c.accountGroupId$) },
);

export const MARKET_ID_TOKEN: InjectionToken<Observable<string>> = new InjectionToken(
  '[AiAssistant]: token for marketId',
  { providedIn: 'root', factory: () => fromConfig((c) => c.marketId$) },
);

export const PARTNER_ID_TOKEN: InjectionToken<Observable<string>> = new InjectionToken(
  '[AiAssistant]: token for partnerId',
  { providedIn: 'root', factory: () => fromConfig((c) => c.partnerId$) },
);

export const AI_DEFAULT_WORKFORCE_TOKEN: InjectionToken<Observable<AiAssistant[]>> = new InjectionToken(
  '[AiAssistant]: token for ai default workforce',
  { providedIn: 'root', factory: () => fromConfig((c) => c.defaultAIWorkforce$) },
);

export const AI_DEFAULT_CONNECTIONS_TOKEN: InjectionToken<Observable<AiConnection[]>> = new InjectionToken(
  '[AiAssistant]: token for ai default connections',
  { providedIn: 'root', factory: () => fromConfig((c) => c.defaultConnections$) },
);

export const CURRENT_USER_ID_TOKEN: InjectionToken<Observable<string>> = new InjectionToken(
  '[AiAssistant]: token for currentUserId',
  { providedIn: 'root', factory: () => fromConfig((c) => c.currentUserId$) },
);

export const VOICE_AI_AVAILABLE_TOKEN: InjectionToken<Observable<boolean>> = new InjectionToken(
  '[AiAssistant]: token for voice ai available',
  { providedIn: 'root', factory: () => fromConfig((c) => c.voiceAIAvailable$) },
);

export const SHOW_CUSTOM_ASSISTANT_TOKEN: InjectionToken<Observable<boolean>> = new InjectionToken(
  '[AiAssistant]: token for show custom assistant',
  { providedIn: 'root', factory: () => fromConfig((c) => c.showCustomAssistant$) },
);

export const NAMESPACE_CONFIG_TOKEN: InjectionToken<Observable<NamespaceConfig>> = new InjectionToken(
  '[AiAssistant]: token for namespace configuration',
  {
    providedIn: 'root',
    factory: () => fromOptionalConfig((c) => asObservable(c.namespaceConfig$)) as Observable<NamespaceConfig>,
  },
);

export const PLATFORM_LOCATION_TOKEN: InjectionToken<PlatformLocation> = new InjectionToken(
  '[AiAssistant]: token for platformLocation',
  { providedIn: 'root', factory: () => fromOptionalConfig((c) => c.platformLocation) as PlatformLocation },
);

export const AI_MARKETER_AVAILABLE_TOKEN: InjectionToken<Observable<boolean>> = new InjectionToken(
  '[AiAssistant]: token for if the AI Marketer is available',
  { providedIn: 'root', factory: () => inject(AI_ASSISTANT_CONFIG_TOKEN).aiMarketerAvailable$ ?? of(false) },
);

export const CRM_UPDATE_MORE_ENTITIES_ENABLED_TOKEN: InjectionToken<Observable<boolean>> = new InjectionToken(
  '[AiAssistant]: token for CRM Update more entities config (opportunities, custom objects)',
  { providedIn: 'root', factory: () => fromOptionalConfig((c) => c.crmUpdateMoreEntitiesEnabled$) ?? of(false) },
);

export const PRIMARY_OPERATING_LANGUAGE_TOKEN: InjectionToken<Observable<string>> = new InjectionToken(
  '[AiAssistant]: token for the business primary operating language',
  { providedIn: 'root', factory: () => fromConfig((c) => c.primaryOperatingLanguage$) },
);

export const NAVIGATE_TO_OPERATING_LANGUAGE_TOKEN: InjectionToken<(() => void) | undefined> = new InjectionToken(
  '[AiAssistant]: token for navigating to the business profile operating language field',
  { providedIn: 'root', factory: () => fromOptionalConfig((c) => c.navigateToOperatingLanguage) ?? undefined },
);

export const ASSISTANT_FIELD_LOCKS_TOKEN: InjectionToken<Observable<AssistantFieldLockMap>> = new InjectionToken(
  '[AiAssistant]: token for the per-field lock map on the assistant edit form',
  { providedIn: 'root', factory: () => inject(AI_ASSISTANT_CONFIG_TOKEN).assistantFieldLocks$ ?? of(NO_FIELD_LOCKS) },
);
